/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { setPendingSaveUser } from "@/lib/offline/pendingSaves";
import type { UserRow } from "@/types/database";

interface AuthState {
  user: UserRow | null;
  /** Email from Supabase auth (session.user.email). */
  authEmail: string | null;
  loading: boolean;
  error: string | null;
  /** True after the first session check has completed (avoids flash redirects). */
  isInitialized: boolean;
}

interface AuthContextValue extends AuthState {
  signOut: () => Promise<void>;
  /** Current organization scope for org-level users. Null for platform users or unlinked org users. */
  organizationId: string | null;
  /** Default path after login by user_type. Only valid when user is set. */
  defaultPath: string;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Result of looking up the app user row.
 * `missing` = the server answered and there is no row; `unreachable` = no answer (weak wifi,
 * timeout, server hiccup), so we learned nothing new about the user.
 */
type UserRowLookup =
  | { kind: "found"; row: UserRow }
  | { kind: "missing" }
  | { kind: "unreachable"; message: string };

/** PostgREST code for `.single()` matching zero rows. */
const NO_ROWS_CODE = "PGRST116";

/** Fetches the app user row from public.users by Supabase auth user id. */
async function fetchUserRow(userId: string): Promise<UserRowLookup> {
  try {
    const { data, error } = await supabase
      .from("users")
      .select("*")
      .eq("id", userId)
      .single();
    if (error) {
      if (error.code === NO_ROWS_CODE) return { kind: "missing" };
      return { kind: "unreachable", message: error.message || "Could not reach the server" };
    }
    if (!data) return { kind: "missing" };
    return { kind: "found", row: data as UserRow };
  } catch (err) {
    return {
      kind: "unreachable",
      message: err instanceof Error ? err.message : "Could not reach the server",
    };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  /**
   * `navigate` changes identity on every route change. Read it through a ref so `loadUser`
   * stays stable; otherwise the auth effect below re-runs on each navigation, sets
   * `loading: true`, and never clears it (pages stick on "Loading…" until a hard refresh).
   */
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const [state, setState] = useState<AuthState>({
    user: null,
    authEmail: null,
    loading: true,
    error: null,
    isInitialized: false,
  });
  const initialCheckDone = useRef(false);
  /** Latest signed-in user, so a failed silent refresh can hand back who is already loaded. */
  const userRef = useRef<UserRow | null>(null);
  userRef.current = state.user;

  /**
   * `silent`: refresh profile without flipping global loading (avoids full-app spinner on token refresh).
   * Reloads role/permissions on each call so staff changes apply on the next request (F2L-61).
   * Returns the loaded row (or null) so callers can react to deactivation.
   */
  const loadUser = useCallback(async (
    userId: string,
    options?: { silent?: boolean },
  ): Promise<UserRow | null> => {
    const silent = Boolean(options?.silent);
    if (!silent) {
      setState((s) => ({ ...s, loading: true, error: null }));
    }
    try {
      const lookup = await fetchUserRow(userId);
      if (lookup.kind === "unreachable" && silent) {
        // Background refresh on a weak connection: keep whoever is signed in. Clearing the user
        // here bounces staff to /login mid-test and unmounts the testing wizard.
        if (import.meta.env.DEV) {
          console.warn("[Fit2Lift auth] silent profile refresh failed; keeping current user", lookup.message);
        }
        return userRef.current;
      }
      const userRow = lookup.kind === "found" ? lookup.row : null;
      if (import.meta.env.DEV) {
        console.log("[Fit2Lift auth] loaded user profile", {
          user_id: userRow?.id ?? userId,
          user_type: userRow?.user_type ?? null,
          organization_id: userRow?.org_id ?? null,
          role_type: userRow?.role_type ?? null,
          deactivated_at: userRow?.deactivated_at ?? null,
        });
      }
      // Deactivated staff must lose the SPA session immediately (ban/signOut may already have run server-side).
      if (userRow?.deactivated_at) {
        try {
          await supabase.auth.signOut();
        } finally {
          setState({
            user: null,
            authEmail: null,
            loading: false,
            error: null,
            isInitialized: true,
          });
          navigateRef.current("/login", { replace: true });
        }
        return null;
      }
      setState((s) => ({
        ...s,
        user: userRow ?? null,
        loading: false,
        error: userRow
          ? null
          : lookup.kind === "unreachable"
            ? lookup.message
            : "User profile not found",
      }));
      return userRow ?? null;
    } catch (err) {
      setState((s) => ({
        ...s,
        ...(silent ? {} : { user: null }),
        loading: false,
        error: silent
          ? s.error
          : err instanceof Error
            ? err.message
            : "Failed to load user",
      }));
      return null;
    } finally {
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => {
    // Single source of truth: initial load via getSession(); subsequent changes via onAuthStateChange.
    setState((s) => ({ ...s, loading: true }));

    void supabase.auth.getSession().then(({ data: { session } }) => {
      if (initialCheckDone.current) {
        // Effect re-ran after the first load finished: undo the `loading: true` set above.
        setState((s) => ({ ...s, loading: false }));
        return;
      }
      if (!session?.user?.id) {
        setState({
          user: null,
          authEmail: null,
          loading: false,
          error: null,
          isInitialized: true,
        });
        initialCheckDone.current = true;
        return;
      }
      setState((s) => ({ ...s, authEmail: session.user.email ?? null }));
      void loadUser(session.user.id).finally(() => {
        initialCheckDone.current = true;
        setState((s) => ({ ...s, isInitialized: true }));
      });
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Let getSession() handle initial session; avoid duplicate fetch.
      if (event === "INITIAL_SESSION") return;

      if (event === "SIGNED_OUT" || !session?.user?.id) {
        setState({
          user: null,
          authEmail: null,
          loading: false,
          error: null,
          isInitialized: true,
        });
        return;
      }

      if (event === "TOKEN_REFRESHED") {
        /** Reload profile so role/permission changes apply without a full re-login (F2L-61). */
        setState((s) => ({
          ...s,
          authEmail: session.user?.email ?? s.authEmail,
        }));
        void loadUser(session.user.id, { silent: true });
        return;
      }

      if (event === "SIGNED_IN" || event === "PASSWORD_RECOVERY") {
        setState((s) => ({ ...s, authEmail: session.user?.email ?? null }));
        void loadUser(session.user.id);
        return;
      }

      if (event === "USER_UPDATED") {
        setState((s) => ({ ...s, authEmail: session.user?.email ?? null }));
        void loadUser(session.user.id, { silent: true });
        return;
      }
    });

    /** Also refresh on tab focus so long-lived tabs pick up staff changes / deactivation. */
    const onFocus = () => {
      void supabase.auth.getSession().then(({ data: { session } }) => {
        if (session?.user?.id) {
          void loadUser(session.user.id, { silent: true });
        }
      });
    };
    window.addEventListener("focus", onFocus);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener("focus", onFocus);
    };
  }, [loadUser]);

  /** Unsent testing saves on this device only run for the staff member who made them. */
  useEffect(() => {
    const userId = state.user?.id ?? null;
    setPendingSaveUser(userId);
    // ROM clips left on this device by this staff member (reload or crash mid-upload): resume them.
    if (userId) {
      void Promise.all([import("@/lib/offline/pendingClips"), import("@/lib/testingMediaUpload")])
        .then(([clips, media]) => clips.resumePendingClips(userId, media.uploadRomMovementVideo))
        .catch((e) => console.warn("[Fit2Lift clip] could not resume stored clips", e));
    }
  }, [state.user?.id]);

  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } finally {
      setState({
        user: null,
        authEmail: null,
        loading: false,
        error: null,
        isInitialized: true,
      });
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  const defaultPath = useMemo((): string => {
    const ut = state.user?.user_type;
    if (!ut) return "/login";
    if (ut === "superadmin" || ut === "admin") return "/admin";
    if (ut === "orgadmin" || ut === "orguser") return "/org";
    return "/org";
  }, [state.user?.user_type]);

  const value: AuthContextValue = useMemo(
    () => ({
      ...state,
      signOut,
      organizationId: state.user?.org_id ?? null,
      defaultPath,
    }),
    [state, signOut, defaultPath]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
